import { supabase } from './supabase'
import { api } from './api'

// Dev-only fallback notice. The marketplace degrades gracefully to the local
// catalog when the backend is absent/empty, so these warns are diagnostics for
// local development only — prod consoles stay clean. Real failures surface via
// Sentry breadcrumbs (see lib/api.js).
function devWarn(...args) {
  if (import.meta.env.DEV) console.warn(...args)
}

// Local marketplace catalog — used as an offline fallback so the marketplace
// stays browsable even before the backend is configured. Prices are in paise.
export const MKT_ITEMS = [
  {
    slug: 'onboarding-setup',
    name: 'Implementation & Onboarding',
    short_desc: 'Guided setup, data migration and go-live support by an OPRAIZ specialist.',
    details: ['Step-by-step workspace setup', 'Legacy data migration', 'Custom forms and templates', 'Go-live day support'],
    kind: 'service',
    price: 1999900,
    currency: 'INR',
    featured: true,
    sort_order: 1,
  },
  {
    slug: 'extra-users-10',
    name: 'Extra User Pack (10 seats)',
    short_desc: 'Add 10 more team seats to your subscription — no annual commitment.',
    details: ['10 additional users', 'Billed once, applied instantly', 'Role-based access included'],
    kind: 'seats',
    price: 999900,
    currency: 'INR',
    featured: true,
    sort_order: 2,
  },
  {
    slug: 'sms-credits-1000',
    name: 'SMS Credits — 1,000 messages',
    short_desc: 'Starter pack for appointment reminders and patient updates.',
    details: ['1,000 SMS credits', 'Valid for 12 months', 'Works across all OPRAIZ products'],
    kind: 'credits',
    price: 149900,
    currency: 'INR',
    featured: false,
    sort_order: 3,
  },
  {
    slug: 'training-workshop',
    name: 'Live Team Training (90 min)',
    short_desc: 'Hands-on online workshop for your staff, tailored to your workflows.',
    details: ['90-minute live session', 'Covers 2 modules of your choice', 'Recorded copy after the session'],
    kind: 'service',
    price: 1499900,
    currency: 'INR',
    featured: false,
    sort_order: 4,
  },
  {
    slug: 'ai-orb-minutes-5000',
    name: 'ORBO DIYA AI — 5,000 minutes',
    short_desc: 'Extra AI assistant minutes for your workspace beyond the plan allowance.',
    details: ['5,000 assistant minutes', 'Powered by Baanzon AI', 'Shared across your workspace'],
    kind: 'credits',
    price: 499900,
    currency: 'INR',
    featured: false,
    sort_order: 5,
  },
  {
    slug: 'white-label-setup',
    name: 'Domain + Branding Setup',
    short_desc: 'Use your own brand and domain for your OPRAIZ workspace.',
    details: ['Custom domain setup', 'Branding and logo configuration', 'Email and SSL included'],
    kind: 'service',
    price: 2499900,
    currency: 'INR',
    featured: false,
    sort_order: 6,
  },
]

export const MKT_KIND_LABELS = {
  service: 'One-time service',
  seats: 'Additional seats',
  credits: 'Credits pack',
  module: 'Module',
}

export const MKT_CATEGORIES = [
  { slug: 'apps', name: 'Partner Apps', description: 'SaaS products published by approved partners' },
  { slug: 'ai', name: 'AI & Automation', description: 'AI tools and assistants on Baanzon AI' },
  { slug: 'integrations', name: 'Integrations', description: 'Connect your existing systems into OPRAIZ' },
  { slug: 'data', name: 'Data & Migration', description: 'Migration, analytics and data services' },
  { slug: 'clinical', name: 'Clinical', description: 'Diagnostics, imaging and clinical utilities' },
]

export const PRODUCT_STATUS_LABELS = {
  draft: 'Draft',
  pending_review: 'Pending review',
  published: 'Published',
  rejected: 'Rejected',
}

export const PRODUCT_STATUS_TONES = {
  draft: 'gray',
  pending_review: 'amber',
  published: 'green',
  rejected: 'red',
}

// Local partner apps — offline fallback so the marketplace stays browsable
// before the backend is configured. Vendor prices stored in paise.
export const VENDOR_APPS = [
  {
    slug: 'pacs-lite',
    name: 'Radiology PACS Lite',
    short_desc: 'View, annotate and share DICOM & X-Ray studies inside SQUAMED.',
    details: [
      'DICOM and standard image formats',
      'Annotate and mark up studies',
      'Share study links with clinicians',
    ],
    kind: 'module',
    price: 149900,
    currency: 'INR',
    status: 'published',
    rating: 4.6,
    reviews_count: 12,
    plans: [
      { name: 'PACS Lite', price_monthly: 1499, features: ['Up to 5 users', '50 studies / month', 'Email support'] },
    ],
    vendor: { business_name: 'PixelGrid Imaging', website: 'https://example.com' },
    category: { slug: 'clinical', name: 'Clinical' },
  },
  {
    slug: 'whatsapp-health',
    name: 'WhatsApp Health Reminders',
    short_desc: 'Automated appointment reminders and follow-ups over WhatsApp.',
    details: [
      'Appointment + follow-up reminders',
      'Two-way patient replies',
      'Zero-touch scheduling updates',
    ],
    kind: 'module',
    price: 99900,
    currency: 'INR',
    status: 'published',
    rating: 4.8,
    reviews_count: 27,
    plans: [
      { name: 'Remind', price_monthly: 999, features: ['1,000 reminders / month', 'WhatsApp delivery', 'Chat support'] },
    ],
    vendor: { business_name: 'MediSend Labs', website: 'https://example.com' },
    category: { slug: 'integrations', name: 'Integrations' },
  },
  {
    slug: 'lab-report-ai',
    name: 'Lab Report AI',
    short_desc: 'Auto-summarise lab PDFs into structured patient records.',
    details: [
      'Parses common lab report PDFs',
      'Flags critical values to staff',
      'Imports into the patient record',
    ],
    kind: 'module',
    price: 249900,
    currency: 'INR',
    status: 'published',
    rating: 4.4,
    reviews_count: 8,
    plans: [
      { name: 'AI Import', price_monthly: 2499, features: ['200 reports / month', 'Critical alerts', 'Baanzon AI powered'] },
    ],
    vendor: { business_name: 'DataForge Health', website: 'https://example.com' },
    category: { slug: 'ai', name: 'AI & Automation' },
  },
]

export const DEFAULT_REVENUE_RATE = 0.2

export const ORDER_STATUS_LABELS = {
  pending: 'Pending payment',
  paid: 'Paid',
  failed: 'Payment failed',
  refunded: 'Refunded',
}

// Format a paise amount as an en-IN currency string, e.g. 1999900 -> "₹19,999"
export function formatPaise(paise, currency = 'INR') {
  const rupees = Math.round(Number(paise) / 100)
  const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₹'
  return `${symbol}${new Intl.NumberFormat('en-IN').format(rupees)}`
}

// OPRAIZ keeps 20%; vendors keep the rest.
export function vendorShare(paise, rate = DEFAULT_REVENUE_RATE) {
  return formatPaise(Math.round(Number(paise) * (1 - rate)))
}

// Best-effort plan price from a product's plans array (paise->monthly).
export function planMonthlyPaise(plan) {
  const monthly = Number(plan?.price_monthly ?? plan?.monthly ?? 0)
  return Math.round(monthly * 100)
}

// Resolves the category slug regardless of whether the row came from the API
// (.marketplace_categories) or the offline fallback (.category or plain slug).
export function vendorAppCategory(app) {
  return (
    app?.marketplace_categories?.slug ||
    app?.category?.slug ||
    app?.category ||
    'other'
  )
}

// Returns true when a catalog entry (first-party item or partner app) mentions
// the query anywhere in its searchable fields.
export function searchMatches(entry, query) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return true
  const haystack = [
    entry?.name,
    entry?.short_desc,
    entry?.kind != null ? MKT_KIND_LABELS[entry.kind] : null,
    entry?.vendor?.business_name,
    Array.isArray(entry?.details) ? entry.details.join(' ') : null,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(q)
}

// Case-insensitive catalog filter (blank query returns everything untouched).
export function filterMarketplace(entries, query) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return entries
  return entries.filter((entry) => searchMatches(entry, q))
}

// Counts marketplace orders by status and derives a paid-conversion percentage.
export function orderFunnel(orders) {
  const list = Array.isArray(orders) ? orders : []
  const counts = { total: list.length, paid: 0, pending: 0, failed: 0, refunded: 0, other: 0 }
  for (const o of list) {
    const s = (o && o.status) || 'pending'
    if (Object.prototype.hasOwnProperty.call(counts, s)) {
      counts[s] += 1
    } else {
      counts.other += 1
    }
  }
  counts.conversion = counts.total > 0 ? Math.round((counts.paid / counts.total) * 100) : 0
  return counts
}

// Revenue per calendar month for paid orders, oldest-first (blank months kept at 0).
export function revenueByMonth(orders, months = 6) {
  const paid = (Array.isArray(orders) ? orders : []).filter((o) => o && o.status === 'paid')
  const now = new Date()
  const series = []
  const index = {}
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    index[key] = series.length
    series.push({ month: d.toLocaleDateString('en-US', { month: 'short' }), revenue: 0 })
  }
  for (const o of paid) {
    const ts = o.created_at ? new Date(o.created_at) : null
    if (!ts || Number.isNaN(ts.getTime())) continue
    const key = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}`
    if (Object.prototype.hasOwnProperty.call(index, key)) {
      series[index[key]].revenue += Number(o.amount || 0)
    }
  }
  return series
}

// Average value of paid orders (amount per order, paise).
export function averageOrderValue(orders) {
  const paid = (Array.isArray(orders) ? orders : []).filter((o) => o && o.status === 'paid')
  if (paid.length === 0) return 0
  return Math.round(paid.reduce((a, o) => a + Number(o.amount || 0), 0) / paid.length)
}

export async function fetchMarketplaceProducts() {
  if (!supabase) {
    devWarn('[marketplace] no backend configured — serving local catalog')
    return MKT_ITEMS
  }
  const { data, error } = await supabase
    .from('marketplace_products')
    .select('*')
    .eq('active', true)
    .eq('status', 'published')
    .order('sort_order', { ascending: true })
  if (error || !data || data.length === 0) {
    if (error) devWarn('[marketplace] fetchMarketplaceProducts error, falling back to local catalog:', error)
    else if (!data || data.length === 0) devWarn('[marketplace] backend returned no products — serving local catalog')
    return MKT_ITEMS
  }
  return data
}

export async function fetchVendorProducts() {
  if (!supabase) {
    devWarn('[marketplace] no backend configured — serving local vendor catalog')
    return VENDOR_APPS
  }
  const { data, error } = await supabase
    .from('marketplace_products')
    .select('*, vendors(business_name, website), marketplace_categories(slug, name)')
    .eq('status', 'published')
    .not('vendor_id', 'is', null)
    .order('created_at', { ascending: true })
  if (error || !data || data.length === 0) {
    if (error) devWarn('[marketplace] fetchVendorProducts error, falling back to local vendor catalog:', error)
    else devWarn('[marketplace] backend returned no vendor products — serving local vendor catalog')
    return VENDOR_APPS
  }
  return data
}

export async function fetchMarketplaceCategories() {
  if (!supabase) {
    devWarn('[marketplace] no backend configured — serving local categories')
    return MKT_CATEGORIES
  }
  const { data, error } = await supabase.from('marketplace_categories').select('*').order('sort_order')
  if (error || !data || data.length === 0) {
    if (error) devWarn('[marketplace] fetchMarketplaceCategories error, falling back to local categories:', error)
    else devWarn('[marketplace] backend returned no categories — serving local categories')
    return MKT_CATEGORIES
  }
  return data
}

export async function fetchVendorProduct(slug, { withReviews = false } = {}) {
  if (!supabase) {
    const offline =
      MKT_ITEMS.find((i) => i.slug === slug) ||
      VENDOR_APPS.find((v) => v.slug === slug)
    if (!offline) return null
    devWarn(`[marketplace] no backend configured — serving local data for '${slug}'`)
    return {
      product: offline,
      reviews: withReviews && offline.reviews ? offline.reviews : [],
      local: true,
    }
  }
  const { data: product, error } = await supabase
    .from('marketplace_products')
    .select('*, vendors(business_name, website), marketplace_categories(slug, name)')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle()
  if (error || !product) return null

  let reviews = []
  if (withReviews) {
    const { data: revs } = await supabase
      .from('marketplace_reviews')
      .select('*')
      .eq('product_id', product.id)
      .order('created_at', { ascending: false })
    reviews = revs || []
  }
  const rating =
    reviews.length > 0
      ? reviews.reduce((a, r) => a + r.rating, 0) / reviews.length
      : Number(product.rating) || 0
  return { product: { ...product, rating }, reviews, local: false }
}

export async function submitVendorReview(productId, rating, comment, userId) {
  if (!supabase) {
    devWarn('[marketplace] submitVendorReview called with no backend configured')
    throw new Error('Reviews are not available right now because the live backend is not configured.')
  }
  let verified = false
  try {
    const { data: customer } = await supabase
      .from('customers').select('id').eq('user_id', userId).maybeSingle()
    if (customer?.id) {
      const { data: sub } = await supabase
        .from('subscriptions').select('id').eq('customer_id', customer.id).eq('status', 'active').maybeSingle()
      verified = Boolean(sub)
    }
  } catch (e) { devWarn('[marketplace] failed to verify purchase, defaulting to unverified:', e) }
  const { error } = await supabase.from('marketplace_reviews').upsert({
    product_id: productId,
    user_id: userId,
    rating,
    comment: comment || null,
    verified,
  }, { onConflict: 'product_id,user_id' })
  if (error) throw error
}

export async function deleteVendorReview(productId, userId) {
  if (!supabase) {
    devWarn('[marketplace] deleteVendorReview called with no backend configured')
    throw new Error('Reviews are not available right now because the live backend is not configured.')
  }
  const { error } = await supabase.from('marketplace_reviews').delete().eq('product_id', productId).eq('user_id', userId)
  if (error) throw error
}

export async function fetchMyVendor() {
  if (!supabase) {
    devWarn('[marketplace] fetchMyVendor called with no backend configured')
    return null
  }
  const { data, error } = await supabase
    .from('vendors')
    .select('*')
    .single()
  if (error) {
    devWarn('[marketplace] fetchMyVendor queried but no vendor row exists:', error.message)
    return null
  }
  return data
}

export async function fetchPublisherData(vendorId) {
  if (!supabase || !vendorId) {
    if (!supabase) devWarn('[marketplace] fetchPublisherData called with no backend configured')
    return { products: [], orders: [], payouts: [], applications: [] }
  }
  const [prodRes, orderRes, payRes, appRes] = await Promise.all([
    supabase.from('marketplace_products').select('*').eq('vendor_id', vendorId).order('created_at', { ascending: false }),
    supabase.from('marketplace_orders').select('*').order('created_at', { ascending: false }).limit(100),
    supabase.from('payouts').select('*').eq('vendor_id', vendorId).order('created_at', { ascending: false }),
    supabase.from('vendor_applications').select('*').order('created_at', { ascending: false }).limit(10),
  ])
  const products = prodRes.data || []
  const slugs = products.map((p) => p.slug)
  const orders = (orderRes.data || []).filter((o) => slugs.includes(o.product_slug))
  return {
    products,
    orders,
    payouts: payRes.data || [],
    applications: appRes.data || [],
  }
}

export function startMarketplaceOrder(payload) {
  return api('marketplace-order', payload)
}

export function submitVendorApplication(payload) {
  return api('marketplace-apply', payload)
}

export function submitVendorProduct(payload) {
  return api('marketplace-submit-product', payload)
}

export function reviewMarketplace(payload) {
  return api('marketplace-review', payload)
}

export async function updateVendorSettings(vendorId, fields) {
  if (!supabase) {
    devWarn('[marketplace] updateVendorSettings called with no backend configured')
    throw new Error('Settings are not available right now because the live backend is not configured.')
  }
  const { data, error } = await supabase.from('vendors').update(fields).eq('id', vendorId).select('*').single()
  if (error) throw error
  return data
}